import React, { useEffect, useState, useRef } from "react";
import { Trophy, Lock, CaretDown, Check, CircleNotch } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import TeamFlag from "./TeamFlag";

export default function BonusBetCard({ tournamentId }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!tournamentId) return;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/tournaments/${tournamentId}/bonus-bet`);
        setData(res.data);
      } catch (err) {
        console.error("Failed to load bonus bet info", err);
        setError(t("could_not_load"));
      } finally {
        setLoading(false);
      }
    })();
  }, [tournamentId]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="bg-neutral-100 border border-neutral-200 rounded-2xl p-4 animate-pulse h-[68px] mb-4" />
    );
  }

  if (error || !data) return null;

  const { prediction, locked, teams } = data;
  const currentWinnerId = prediction?.predicted_winner_id || null;
  const pointsAwarded = prediction?.points;

  const selectedTeam = teams.find((t) => t.team_id === currentWinnerId);

  const handleSelectTeam = async (teamId) => {
    if (locked) return;
    setSaving(true);
    setSaved(false);
    setIsOpen(false);
    try {
      await api.post(`/tournaments/${tournamentId}/bonus-bet`, {
        predicted_winner_id: teamId,
      });
      setData((prev) => ({
        ...prev,
        prediction: {
          predicted_winner_id: teamId,
          points: prev.prediction?.points ?? null,
        },
      }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to place bonus bet", err);
      alert(t("error_saving_prediction"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm text-[#02371b] mb-4 relative">
      <div className="flex items-center justify-between gap-2.5 mb-2.5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500">
            <Trophy size={18} weight="fill" />
          </div>
          <div>
            <h3 className="font-display text-sm font-bold text-[#02371b]">
              {t("bonus_bet_title")}
            </h3>
          </div>
        </div>
        <div className="flex-shrink-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 border border-amber-200 text-amber-600">
            {t("bonus_bet_reward")}
          </span>
        </div>
      </div>

      <div className="relative" ref={dropdownRef}>
        {locked ? (
          <div className="w-full bg-neutral-50 border border-neutral-100 rounded-xl px-3 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 min-w-0">
              {selectedTeam ? (
                <>
                  <TeamFlag team={selectedTeam} size={20} />
                  <span className="font-bold text-neutral-800 truncate">
                    {selectedTeam.name}
                  </span>
                </>
              ) : (
                <span className="text-neutral-400">
                  {t("bonus_bet_no_prediction")}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-neutral-400 text-xs font-bold uppercase tracking-wider pl-2">
              {pointsAwarded !== null ? (
                pointsAwarded > 0 ? (
                  <span className="text-amber-600 font-mono font-bold">
                    {t("bonus_bet_points_won")}
                  </span>
                ) : (
                  <span className="text-neutral-400 font-mono">
                    {t("bonus_bet_points_lost")}
                  </span>
                )
              ) : (
                <>
                  <Lock size={12} weight="bold" />
                  <span>{t("locked")}</span>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              disabled={saving}
              className="w-full bg-white hover:bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2 flex items-center justify-between text-sm transition-all focus:outline-none focus:ring-1 focus:ring-[#41a42b]/50 text-[#02371b]"
            >
              <div className="flex items-center gap-2 min-w-0">
                {selectedTeam ? (
                  <>
                    <TeamFlag team={selectedTeam} size={20} />
                    <span className="font-bold text-[#02371b] truncate">
                      {selectedTeam.name}
                    </span>
                  </>
                ) : (
                  <span className="text-neutral-400">
                    {t("bonus_bet_select_winner")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 text-neutral-400">
                {saving && <CircleNotch size={14} className="animate-spin text-[#41a42b]" />}
                {saved && <Check size={14} className="text-[#41a42b]" />}
                <CaretDown size={14} weight="bold" />
              </div>
            </button>

            {isOpen && (
              <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-neutral-200 rounded-xl shadow-xl z-50 p-1">
                {teams.map((t) => (
                  <div
                    key={t.team_id}
                    onClick={() => handleSelectTeam(t.team_id)}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-all hover:bg-neutral-50 text-[#02371b] ${
                      t.team_id === currentWinnerId
                        ? "bg-emerald-50/50 font-bold hover:bg-emerald-50/50"
                        : ""
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <TeamFlag team={t} size={20} />
                      <span className="truncate">{t.name}</span>
                    </div>
                    {t.team_id === currentWinnerId && (
                      <Check size={14} weight="bold" className="text-[#41a42b] flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
